import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Wallet, Users, CheckCircle2, AlertCircle, Edit } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StaffBankDetails() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ account_name: '', sort_code: '', account_number: '', bank_name: '', email_transfer: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchStaff(); }, []);
  const fetchStaff = async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API_URL}/api/admin/staff-bank-details`, { withCredentials: true }); setStaff(data); }
    catch { toast.error('Failed to load staff'); }
    setLoading(false);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm(s.bank_details || { account_name: '', sort_code: '', account_number: '', bank_name: '', email_transfer: '' });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/staff-bank-details/${editing.id}`, form, { withCredentials: true });
      toast.success('Bank details updated');
      setEditing(null);
      fetchStaff();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="staff-bank-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">Staff Bank Details</h1>
          <p className="text-muted-foreground mt-1">Manage payroll bank information for staff</p>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
        ) : staff.length === 0 ? (
          <div className="text-center py-16 glass-card rounded-xl border border-border">
            <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground">No staff found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {staff.map((s) => (
              <Card key={s.id} className="glass-card border-border" data-testid={`staff-bank-${s.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-muted"><Wallet className="w-5 h-5 text-[#FF6F61]" /></div>
                    <div>
                      <p className="text-foreground font-medium">{s.name}</p>
                      <p className="text-sm text-muted-foreground">{s.email} | {s.staff_number} | <span className="capitalize">{s.role?.replace('_', ' ')}</span></p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {s.bank_details ? (
                      <span className="flex items-center gap-1 text-xs text-[#A8E6CF]"><CheckCircle2 className="w-3 h-3" />On file</span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-amber-400"><AlertCircle className="w-3 h-3" />Missing</span>
                    )}
                    <Button size="sm" variant="outline" className="border-border text-foreground gap-1" onClick={() => openEdit(s)}><Edit className="w-3 h-3" />{s.bank_details ? 'Edit' : 'Add'}</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="bg-background border-border max-w-md">
            <DialogHeader><DialogTitle className="text-xl font-heading text-foreground">Bank Details — {editing?.name}</DialogTitle></DialogHeader>
            <form onSubmit={handleSave} className="space-y-4 py-2">
              <div className="space-y-2"><Label className="text-foreground">Account Holder Name</Label><Input value={form.account_name} onChange={(e) => setForm(p => ({ ...p, account_name: e.target.value }))} className="bg-muted border-border text-foreground" required /></div>
              <div className="space-y-2"><Label className="text-foreground">Bank Name</Label><Input value={form.bank_name} onChange={(e) => setForm(p => ({ ...p, bank_name: e.target.value }))} className="bg-muted border-border text-foreground" required /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label className="text-foreground">Sort Code</Label><Input value={form.sort_code} onChange={(e) => setForm(p => ({ ...p, sort_code: e.target.value }))} className="bg-muted border-border text-foreground" placeholder="00-00-00" /></div>
                <div className="space-y-2"><Label className="text-foreground">Account Number</Label><Input value={form.account_number} onChange={(e) => setForm(p => ({ ...p, account_number: e.target.value }))} className="bg-muted border-border text-foreground" /></div>
              </div>
              <div className="space-y-2"><Label className="text-foreground">Email for Bank Transfer (optional)</Label><Input type="email" value={form.email_transfer} onChange={(e) => setForm(p => ({ ...p, email_transfer: e.target.value }))} className="bg-muted border-border text-foreground" placeholder="payee@email.com" data-testid="email-transfer-input" /></div>
              <Button type="submit" disabled={saving} className="w-full btn-primary">{saving ? 'Saving...' : 'Save Details'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
